// Minimap (spec §12, phase 6): the whole board in miniature with the current
// viewport outlined. The content is cached as a bitmap and redrawn at most
// every 250 ms after changes; panning only redraws the viewport outline.
// Click or drag on it to move the view. Toggle with M.
import type { BoardObject } from '../../shared/types.ts';
import { CARD } from '../../shared/cards.ts';
import type { Editor } from '../editor.ts';
import { h } from '../util/dom.ts';
import { union, type Rect } from '../util/geometry.ts';

const W = 200, H = 136;               // CSS px
const REDRAW_MS = 250;
const PAD = 0.08;                     // fraction of the mapped area kept as margin
const STORAGE_KEY = 'minimap-hidden';

function colorOf(o: BoardObject): string | null {
  switch (o.type) {
    case 'sticky': return o.color;
    case 'shape': return o.fill !== 'transparent' ? o.fill : o.stroke;
    case 'freehand': return o.stroke;
    case 'image': return '#C7CCD6';
    case 'text': return '#9AA0A6';
    case 'frame':
    case 'audio':
    case 'card': return '#FFFFFF';
    case 'connector': return null;
  }
}

export function createMinimap(ed: Editor): HTMLElement {
  const canvas = h('canvas', { class: 'minimap-canvas', 'aria-label': 'Minimap: click to move the view' });
  const wrap = h('div', { class: 'panel minimap' }, [canvas]);
  const ctx = canvas.getContext('2d')!;
  let dpr = 0;
  let map: { x: number; y: number; k: number } | null = null; // world → minimap px (CSS)
  let content: OffscreenCanvas | null = null;
  let timer = 0;

  try { wrap.hidden = localStorage.getItem(STORAGE_KEY) === '1'; } catch { /* default shown */ }

  const worldRect = (): Rect => {
    const r = union([ed.contentBounds() ?? ed.viewport(), ed.viewport()])!;
    return { x: r.x - r.w * PAD, y: r.y - r.h * PAD, w: r.w * (1 + 2 * PAD), h: r.h * (1 + 2 * PAD) };
  };

  function drawContent(): void {
    timer = 0;
    if (wrap.hidden) return;
    dpr = devicePixelRatio || 1;
    canvas.width = Math.round(W * dpr);
    canvas.height = Math.round(H * dpr);
    const r = worldRect();
    const k = Math.min(W / r.w, H / r.h);
    map = { x: r.x - (W / k - r.w) / 2, y: r.y - (H / k - r.h) / 2, k };
    content = new OffscreenCanvas(canvas.width, canvas.height);
    const c = content.getContext('2d')!;
    c.scale(dpr, dpr);
    for (const id of ed.store.orderedIds()) {
      const o = ed.store.get(id)!;
      const color = colorOf(o);
      if (!color) continue;
      const b = ed.boundsOf(o);
      const x = (b.x - map.x) * k, y = (b.y - map.y) * k;
      const w = Math.max(1, b.w * k), hh = Math.max(1, b.h * k);
      c.fillStyle = color;
      c.fillRect(x, y, w, hh);
      if (o.type === 'frame' || o.type === 'card') {
        c.strokeStyle = 'rgba(0,0,0,0.18)';
        c.lineWidth = 1;
        c.strokeRect(x, y, w, hh);
      }
      if (o.type === 'card') {
        c.fillStyle = o.color;
        c.fillRect(x, y, w, Math.min(hh, Math.max(1.5, CARD.strip * k)));
      }
    }
    drawView();
  }

  function drawView(): void {
    if (wrap.hidden || !map || !content) return;
    const v = ed.viewport();
    const m = map;
    // Recompute the mapping if the view has wandered outside the mapped area.
    if (v.x < m.x || v.y < m.y || v.x + v.w > m.x + W / m.k || v.y + v.h > m.y + H / m.k) return schedule(0);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(content, 0, 0);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const x = (v.x - m.x) * m.k, y = (v.y - m.y) * m.k, w = v.w * m.k, hh = v.h * m.k;
    ctx.fillStyle = 'rgba(66, 98, 255, 0.08)';
    ctx.fillRect(x, y, w, hh);
    ctx.strokeStyle = '#4262FF';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(x, y, w, hh);
  }

  function schedule(ms = REDRAW_MS): void {
    if (timer && ms) return;
    clearTimeout(timer);
    timer = window.setTimeout(drawContent, ms);
  }

  // Click/drag: centre the view on that point.
  const moveTo = (e: PointerEvent) => {
    if (!map) return;
    const r = canvas.getBoundingClientRect();
    const wx = map.x + (e.clientX - r.left) / map.k;
    const wy = map.y + (e.clientY - r.top) / map.k;
    const v = ed.viewport();
    ed.camera.set(wx - v.w / 2, wy - v.h / 2, ed.camera.zoom);
  };
  canvas.addEventListener('pointerdown', (e) => {
    canvas.setPointerCapture(e.pointerId);
    moveTo(e);
  });
  canvas.addEventListener('pointermove', (e) => { if (canvas.hasPointerCapture(e.pointerId)) moveTo(e); });

  ed.store.subscribe(() => schedule());
  ed.events.on('camera', drawView);
  ed.events.on('command', (c) => {
    if (c !== 'minimap') return;
    wrap.hidden = !wrap.hidden;
    try { localStorage.setItem(STORAGE_KEY, wrap.hidden ? '1' : '0'); } catch { /* ignore */ }
    if (!wrap.hidden) schedule(0);
  });
  window.addEventListener('resize', () => schedule());
  schedule(0);
  return wrap;
}
